import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Check, Info, AlertCircle } from 'lucide-react'
import { SubmitButton } from '@/components/form'
import { useTranslation } from 'react-i18next'
import { ROUTES } from '@/constants/routes'
import { OfferType, SubscriptionInterval } from '@/constants/wizard'
import { AdherentStructureCombobox } from './AdherentStructureCombobox'
import { type AdherentStructure } from '@/constants/structures'
import { usersService } from '@/services/users'
import { getApiErrorMessage } from '@/utils/errors'
import { AxiosError } from 'axios'
import { RppsFunction } from '@/constants/account'
import { formatPrice } from '@/utils/pricing'

interface RPPSVerificationStepProps {
  rppsNumber?: string
  onRppsNumberChange: (value: string) => void
  promoCode?: string
  onPromoCodeChange: (value: string) => void
  selectedOffer?: OfferType | null
  selectedStructure?: AdherentStructure | null
  onStructureChange?: (structure: AdherentStructure | null) => void
  onNext: () => void
  onRppsVerified?: (data: {
    nom_exercice: string
    prenom_exercice: string
    function: string | null
  }) => void
  onRegistrationCodeVerified?: (code: string) => void
  onPromoCodeVerified?: (freeMonths: number | null) => void
  price?: number | null
  onPriceChange?: (price: number | null) => void
  getPriceForFunction?: (rppsFunction: string, interval: SubscriptionInterval) => number | null
  interval?: SubscriptionInterval
}

interface UserInfo {
  lastName: string
  firstName: string
  profession: string
  status: string
  price: number
}

const REGISTRATION_CODE_REGEX = /^\d{6}$/

export function RPPSVerificationStep({
  rppsNumber = '',
  onRppsNumberChange,
  promoCode = '',
  onPromoCodeChange,
  selectedOffer = null,
  selectedStructure = null,
  onStructureChange,
  onNext,
  onRppsVerified,
  onRegistrationCodeVerified,
  onPromoCodeVerified,
  price = null,
  onPriceChange,
  getPriceForFunction,
  interval = SubscriptionInterval.YEARLY,
}: RPPSVerificationStepProps) {
  const { t } = useTranslation('wizard')
  const [isValidated, setIsValidated] = useState(false)
  const [acceptCGU, setAcceptCGU] = useState(false)
  const [acceptCommunications, setAcceptCommunications] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null)
  const [showWarning, setShowWarning] = useState(false)
  const [showInternMismatch, setShowInternMismatch] = useState(false)
  const [lastValidatedRpps, setLastValidatedRpps] = useState<string>('')
  const [promoCodeError, setPromoCodeError] = useState<string | null>(null)
  const [promoCodeValid, setPromoCodeValid] = useState(false)
  const [promoCodeFreeMonths, setPromoCodeFreeMonths] = useState<number | null>(null)

  const [hasNoRpps, setHasNoRpps] = useState(false)
  const [registrationCode, setRegistrationCode] = useState('')
  const [codeValidated, setCodeValidated] = useState(false)
  const [lastValidatedCode, setLastValidatedCode] = useState('')

  const handleRppsChange = (value: string) => {
    onRppsNumberChange(value)
    if (value !== lastValidatedRpps && isValidated) {
      setIsValidated(false)
      setUserInfo(null)
      setShowWarning(false)
      setShowInternMismatch(false)
      setError(null)
      setAcceptCGU(false)
      setAcceptCommunications(false)
      setPromoCodeError(null)
      setPromoCodeValid(false)
      setPromoCodeFreeMonths(null)
      if (onPromoCodeVerified) onPromoCodeVerified(null)
    }
  }

  const handlePromoCodeChange = (value: string) => {
    onPromoCodeChange(value)
    if (promoCodeError || promoCodeValid) {
      setPromoCodeError(null)
      setPromoCodeValid(false)
      setPromoCodeFreeMonths(null)
      if (onPromoCodeVerified) onPromoCodeVerified(null)
    }
  }

  const handleRegistrationCodeChange = (value: string) => {
    setRegistrationCode(value)
    if (value !== lastValidatedCode && codeValidated) {
      setCodeValidated(false)
      setError(null)
      setAcceptCGU(false)
      setAcceptCommunications(false)
    }
  }

  const handleToggleNoRpps = (checked: boolean) => {
    setHasNoRpps(checked)
    setError(null)
    setIsValidated(false)
    setUserInfo(null)
    setShowWarning(false)
    setShowInternMismatch(false)
    setAcceptCGU(false)
    setAcceptCommunications(false)
    setCodeValidated(false)
    setLastValidatedCode('')
  }

  const handleValidateRpps = async () => {
    if (rppsNumber.trim().length === 0) return

    setIsLoading(true)
    setError(null)
    setShowWarning(false)
    setShowInternMismatch(false)
    setPromoCodeError(null)
    setPromoCodeValid(false)
    setPromoCodeFreeMonths(null)

    try {
      const trimmedPromoCode = promoCode.trim()
      const response = await usersService.verifyRpps({
        rpps: rppsNumber.trim(),
        ...(trimmedPromoCode && { promo_code: trimmedPromoCode }),
      })

      if (trimmedPromoCode) {
        if (response.promo_code_error) {
          setPromoCodeError(response.promo_code_error)
        } else {
          setPromoCodeValid(true)
          setPromoCodeFreeMonths(response.promo_code_free_months ?? null)
          if (onPromoCodeVerified) onPromoCodeVerified(response.promo_code_free_months ?? null)
        }
      }

      const rppsData = response.data

      const isInternSelected = selectedOffer === OfferType.INTERN
      const isFunctionIntern = rppsData.fonction?.toLowerCase() === RppsFunction.INTERN

      if (isInternSelected && !isFunctionIntern) {
        setShowWarning(true)
      } else if (!isInternSelected && isFunctionIntern && selectedOffer !== OfferType.MEMBER) {
        setShowInternMismatch(true)
      }

      let actualPrice = price || 0
      const isInternMismatch = selectedOffer === OfferType.PRACTITIONER && isFunctionIntern
      if (
        selectedOffer !== OfferType.MEMBER &&
        !isInternMismatch &&
        getPriceForFunction &&
        rppsData.fonction
      ) {
        const calculatedPrice = getPriceForFunction(rppsData.fonction, interval)
        if (calculatedPrice !== null) {
          actualPrice = calculatedPrice
          if (onPriceChange) {
            onPriceChange(calculatedPrice)
          }
        }
      }

      setUserInfo({
        lastName: rppsData.nom_exercice || '',
        firstName: rppsData.prenom_exercice || '',
        profession: rppsData.profession || '',
        status: rppsData.fonction || '',
        price: actualPrice,
      })

      if (onRppsVerified) {
        onRppsVerified({
          nom_exercice: rppsData.nom_exercice,
          prenom_exercice: rppsData.prenom_exercice,
          function: rppsData.fonction,
        })
      }

      setLastValidatedRpps(rppsNumber.trim())
      setIsValidated(true)
    } catch (err) {
      const axiosError = err as AxiosError
      const status = axiosError.response?.status

      if (status === 404) {
        setError(getApiErrorMessage(err, t('rppsVerification.errors.rppsNotFound')))
      } else if (status === 409) {
        setError(getApiErrorMessage(err, t('rppsVerification.errors.userAlreadyExists')))
      } else {
        setError(getApiErrorMessage(err, t('rppsVerification.errors.generic')))
      }
      setIsValidated(false)
    } finally {
      setIsLoading(false)
    }
  }

  const handleValidateRegistrationCode = async () => {
    if (!REGISTRATION_CODE_REGEX.test(registrationCode)) return

    setIsLoading(true)
    setError(null)

    try {
      await usersService.verifyRegistrationCode({ code: registrationCode })

      if (onRegistrationCodeVerified) {
        onRegistrationCodeVerified(registrationCode)
      }

      setLastValidatedCode(registrationCode)
      setCodeValidated(true)
    } catch (err) {
      setError(getApiErrorMessage(err, t('rppsVerification.errors.invalidRegistrationCode')))
      setCodeValidated(false)
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && rppsNumber.trim().length > 0 && !isValidated) {
      e.preventDefault()
      handleValidateRpps()
    }
  }

  const handleCodeInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && REGISTRATION_CODE_REGEX.test(registrationCode) && !codeValidated) {
      e.preventDefault()
      handleValidateRegistrationCode()
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (hasNoRpps) {
      if (!codeValidated) {
        handleValidateRegistrationCode()
      } else if (acceptCGU) {
        onNext()
      }
    } else {
      if (!isValidated) {
        handleValidateRpps()
      } else if (acceptCGU) {
        onNext()
      }
    }
  }

  const isCodeFlowValidated = codeValidated && registrationCode === lastValidatedCode

  return (
    <form onSubmit={handleSubmit} className="mx-auto max-w-2xl">
      <div className="mb-10 rounded-2xl border border-[#E9E9E9] bg-white p-6 shadow-[0px_-1px_4px_rgba(0,0,0,0.25)]">
        <h2 className="mb-4 text-base font-medium text-black">
          {t('rppsVerification.sectionTitle')}
        </h2>

        {/* Toggle "I don't have an RPPS number" — only for the PRACTITIONER offer */}
        {selectedOffer === OfferType.PRACTITIONER && (
          <label className="mb-4 flex cursor-pointer items-center gap-3">
            <div className="relative">
              <input
                type="checkbox"
                className="peer sr-only"
                checked={hasNoRpps}
                onChange={e => handleToggleNoRpps(e.target.checked)}
              />
              <div className="h-6 w-11 rounded-full bg-gray-200 transition-colors peer-checked:bg-[#b11739]" />
              <div className="absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5" />
            </div>
            <span className="text-sm font-normal text-black">
              {t('rppsVerification.noRppsToggle')}
            </span>
          </label>
        )}

        {error && (
          <div className="mb-4 flex items-start gap-3 rounded-lg border border-red-300 bg-red-50 p-4">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
            <p className="text-sm font-normal text-red-600">{error}</p>
          </div>
        )}

        {hasNoRpps ? (
          /* ── No-RPPS flow ── */
          <div>
            <div className="mb-4 flex items-start gap-3 rounded-lg border-[0.3px] border-[#B45628] bg-[#FEFCEE] p-4">
              <Info className="mt-0.5 h-5 w-5 shrink-0 text-[#B45628]" />
              <p className="text-sm font-normal text-[#B45628]">
                {t('rppsVerification.noRppsInfoBannerBefore')}
                <a href="mailto:contact@ebim.ai" className="font-medium text-[#b11739] underline">
                  {t('rppsVerification.noRppsInfoBannerEmail')}
                </a>
                {t('rppsVerification.noRppsInfoBannerAfter')}
              </p>
            </div>

            <label
              htmlFor="registrationCode"
              className="mb-2 block text-sm font-semibold text-black"
            >
              {t('rppsVerification.registrationCodeLabel')}
              <span className="text-primary">*</span>
            </label>
            <input
              id="registrationCode"
              type="text"
              value={registrationCode}
              onChange={e => handleRegistrationCodeChange(e.target.value)}
              onKeyDown={handleCodeInputKeyDown}
              placeholder={t('rppsVerification.registrationCodePlaceholder')}
              maxLength={6}
              className={`mb-2 w-full rounded-lg px-4 py-3.5 ${
                isCodeFlowValidated
                  ? 'border border-[#41B95D] bg-[#EDF9F0]'
                  : 'border border-[#858C94] bg-white'
              }`}
            />
            {isCodeFlowValidated && (
              <div className="mb-4 flex items-center gap-2 rounded-lg bg-[#EDF9F0] px-4 py-2">
                <Check className="h-4 w-4 text-[#41B95D]" />
                <span className="text-sm font-normal text-[#41B95D]">
                  {t('rppsVerification.registrationCodeValidMessage')}
                </span>
              </div>
            )}
          </div>
        ) : (
          /* ── Normal RPPS flow ── */
          <div>
            {showWarning && (
              <div className="mb-4 flex items-start gap-3 rounded-lg border-[0.3px] border-[#B45628] bg-[#FEFCEE] p-4">
                <Info className="mt-0.5 h-5 w-5 shrink-0 text-[#B45628]" />
                <p className="text-sm font-normal text-[#B45628]">
                  {t('rppsVerification.warning.functionMismatch')}
                </p>
              </div>
            )}

            {showInternMismatch && (
              <div className="mb-4 flex items-start gap-3 rounded-lg border-[0.3px] border-[#B45628] bg-[#FEFCEE] p-4">
                <Info className="mt-0.5 h-5 w-5 shrink-0 text-[#B45628]" />
                <p className="text-sm font-normal text-[#B45628]">
                  {t('rppsVerification.warning.internMismatch')}
                </p>
              </div>
            )}

            {isValidated &&
              !showWarning &&
              userInfo &&
              (userInfo.status ?? '').toLowerCase() !== RppsFunction.INTERN &&
              selectedOffer !== OfferType.MEMBER &&
              selectedOffer !== OfferType.PRACTITIONER && (
                <div className="mb-4 flex items-start gap-3 rounded-lg border-[0.3px] border-[#B45628] bg-[#FEFCEE] p-4">
                  <Info className="mt-0.5 h-5 w-5 shrink-0 text-[#B45628]" />
                  <p className="text-sm font-normal text-[#B45628]">
                    {t('rppsVerification.infoBanner')}
                  </p>
                </div>
              )}

            <label htmlFor="rpps" className="mb-2 block text-sm font-semibold text-black">
              {t('rppsVerification.label')}
              <span className="text-primary">*</span>
            </label>
            <input
              id="rpps"
              type="text"
              value={rppsNumber}
              onChange={e => handleRppsChange(e.target.value)}
              onKeyDown={handleInputKeyDown}
              placeholder={t('rppsVerification.placeholder')}
              className={`mb-2 w-full rounded-lg px-4 py-3.5 ${
                isValidated && rppsNumber === lastValidatedRpps
                  ? 'border border-[#41B95D] bg-[#EDF9F0]'
                  : 'border border-[#858C94] bg-white'
              }`}
              required
            />
            {isValidated && rppsNumber === lastValidatedRpps && (
              <div className="mb-4 flex items-center gap-2 rounded-lg bg-[#EDF9F0] px-4 py-2">
                <Check className="h-4 w-4 text-[#41B95D]" />
                <span className="text-sm font-normal text-[#41B95D]">
                  {t('rppsVerification.validMessage')}
                </span>
              </div>
            )}

            <label htmlFor="promoCode" className="mb-2 block text-sm font-semibold text-black">
              {t('rppsVerification.jetonLabel')}
            </label>
            <input
              id="promoCode"
              type="text"
              value={promoCode}
              onChange={e => handlePromoCodeChange(e.target.value)}
              onKeyDown={handleInputKeyDown}
              placeholder={t('rppsVerification.jetonPlaceholder')}
              maxLength={6}
              className="mb-2 w-full rounded-lg border border-[#858C94] bg-white px-4 py-3.5"
            />
            {promoCodeValid && (
              <div className="mb-4 flex items-center gap-2 rounded-lg bg-[#EDF9F0] px-4 py-2">
                <Check className="h-4 w-4 text-[#41B95D]" />
                <span className="text-sm font-normal text-[#41B95D]">
                  {t('rppsVerification.jetonValidMessage', { count: promoCodeFreeMonths ?? 0 })}
                </span>
              </div>
            )}
            {promoCodeError && (
              <div className="mb-4 flex items-start gap-3 rounded-lg border-[0.3px] border-[#B45628] bg-[#FEFCEE] p-4">
                <Info className="mt-0.5 h-5 w-5 shrink-0 text-[#B45628]" />
                <p className="text-sm font-normal text-[#B45628]">{promoCodeError}</p>
              </div>
            )}
          </div>
        )}

        {!hasNoRpps && selectedOffer === OfferType.MEMBER && (
          <>
            <AdherentStructureCombobox
              selectedStructure={selectedStructure || null}
              onStructureSelect={onStructureChange || (() => {})}
            />
            <div className="mb-6 flex items-center gap-3 rounded-lg border-[0.3px] border-[#B45628] bg-[#FEFCEE] px-5 py-4">
              <Info className="mt-0.5 h-5 w-5 shrink-0 text-[#B45628]" />
              <p className="text-[13px] font-normal text-[#B45628]">
                {t('rppsVerification.structureAdhesionWarning')}
              </p>
            </div>
          </>
        )}

        {!hasNoRpps && isValidated && rppsNumber === lastValidatedRpps && userInfo && (
          <div className="mb-6 rounded-lg border border-[#F7F7F7] bg-[#F8F8F8] p-4">
            <div className="grid grid-cols-5 gap-4">
              <div>
                <div className="mb-1 text-sm font-normal text-gray-600">
                  {t('rppsVerification.userInfo.lastName')}
                </div>
                <div className="text-sm font-normal text-black">{userInfo.lastName}</div>
              </div>
              <div>
                <div className="mb-1 text-sm font-normal text-gray-600">
                  {t('rppsVerification.userInfo.firstName')}
                </div>
                <div className="text-sm font-normal text-black">{userInfo.firstName}</div>
              </div>
              <div>
                <div className="mb-1 text-sm font-normal text-gray-600">
                  {t('rppsVerification.userInfo.profession')}
                </div>
                <div className="text-sm font-normal text-black">{userInfo.profession || '-'}</div>
              </div>
              <div>
                <div className="mb-1 text-sm font-normal text-gray-600">
                  {t('rppsVerification.userInfo.status')}
                </div>
                <div className="text-sm font-normal text-black">{userInfo.status}</div>
              </div>
              <div>
                <div className="mb-1 text-sm font-normal text-gray-600">
                  {t('rppsVerification.userInfo.selectedOffer')}
                </div>
                <div className="flex flex-wrap items-baseline gap-1 text-[14px] leading-normal font-medium">
                  {selectedOffer ? (
                    <span className="text-[#B11739]">{formatPrice(userInfo.price)}€</span>
                  ) : (
                    <span className="text-black">-</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {!hasNoRpps &&
          selectedOffer === OfferType.INTERN &&
          !(isValidated && rppsNumber === lastValidatedRpps) && (
            <a
              href="https://annuaire.esante.gouv.fr/"
              target="_blank"
              rel="noopener noreferrer"
              className="mb-6 block rounded-md bg-[#F2F6FF] px-4 py-2.5 text-sm font-normal text-[#2062F6] underline"
            >
              {t('rppsVerification.helpLink')}
            </a>
          )}

        {((hasNoRpps && isCodeFlowValidated) ||
          (!hasNoRpps && isValidated && rppsNumber === lastValidatedRpps)) && (
          <div className="mb-6 space-y-4">
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={acceptCGU}
                onChange={e => setAcceptCGU(e.target.checked)}
                className="mt-0.5 h-4 w-4 cursor-pointer rounded border-gray-300 accent-[#2062F6] focus:ring-[#2062F6]"
              />
              <span className="text-sm font-normal text-black">
                {t('rppsVerification.checkboxes.cgu.prefix')}{' '}
                <Link
                  to={ROUTES.CGU}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#2062F6] underline"
                >
                  {t('rppsVerification.checkboxes.cgu.link')}
                </Link>
              </span>
            </label>
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={acceptCommunications}
                onChange={e => setAcceptCommunications(e.target.checked)}
                className="mt-0.5 h-4 w-4 cursor-pointer rounded border-gray-300 accent-[#2062F6] focus:ring-[#2062F6]"
              />
              <span className="text-sm font-normal text-black">
                {t('rppsVerification.checkboxes.communications')}
              </span>
            </label>
          </div>
        )}

        <SubmitButton
          isLoading={isLoading}
          loadingLabel={t('rppsVerification.structureLoading')}
          disabled={
            hasNoRpps
              ? !REGISTRATION_CODE_REGEX.test(registrationCode) ||
                (isCodeFlowValidated && !acceptCGU)
              : (selectedOffer === OfferType.MEMBER && !selectedStructure) ||
                (isValidated && rppsNumber === lastValidatedRpps && !acceptCGU) ||
                rppsNumber.trim().length === 0
          }
        >
          {hasNoRpps
            ? isCodeFlowValidated
              ? t('rppsVerification.nextButton')
              : t('rppsVerification.submitButton')
            : isValidated && rppsNumber === lastValidatedRpps
              ? t('rppsVerification.nextButton')
              : t('rppsVerification.submitButton')}
        </SubmitButton>
      </div>
    </form>
  )
}
